/* RecommendedForYou — session-aware cross-division recommendation strip.
 * Mounted on each division landing (Home, Systems, Services, Academy)
 * above CrossDivisionStrip. Pulls /api/recommendations?session_id=…&division=…
 * which scores 3 curated items adapted to the visitor's recent activity.
 *
 * UX notes:
 *   • If the visitor has rejected cookies, we still call the endpoint (it's
 *     a lightweight public lookup, no PII), but pass no session_id so the
 *     response is the static curated list — privacy-friendly default.
 *   • Each card click logs a `recommendation_click` interaction event so
 *     the admin can later measure conversion alongside the cross-division
 *     traffic widget. Gated by cookie consent like every other tracker.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import { getSessionId } from '../utils/session';
import { getCookieConsent } from './CookieBanner';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const DIVISION_ACCENTS = {
  systems:  { tag: 'Systems',  color: '#3b82f6' },
  services: { tag: 'Services', color: '#10b981' },
  academy:  { tag: 'Academy',  color: '#f5a623' },
  home:     { tag: 'ZHELTA',   color: '#94a3b8' },
};

export default function RecommendedForYou({ division = 'home', heading }) {
  const [items, setItems] = useState([]);
  const [personalised, setPersonalised] = useState(false);

  // Only attach session_id when the user has accepted cookies. Otherwise
  // the backend silently falls back to the static curated list.
  const sessionId = useMemo(() => {
    if (getCookieConsent()?.choice === 'accepted') {
      try { return getSessionId(); } catch { return null; }
    }
    return null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ division, limit: '3' });
    if (sessionId) params.set('session_id', sessionId);

    fetch(`${API}/recommendations?${params}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const list = Array.isArray(data.items) ? data.items : [];
        setItems(list);
        setPersonalised(!!data.personalised);

        // Fire one impression event per rendered card (consent-gated).
        // De-duped at the session level so a quick re-render doesn't
        // inflate the count — the SEEN_KEY persists per browser tab.
        if (list.length && getCookieConsent()?.choice === 'accepted') {
          const SEEN_KEY = '__zhelta_reco_seen__';
          if (!window[SEEN_KEY]) window[SEEN_KEY] = new Set();
          const seen = window[SEEN_KEY];
          list.forEach((it) => {
            const tag = `${window.location.pathname}::${it.id}`;
            if (seen.has(tag)) return;
            seen.add(tag);
            fetch(`${API}/analytics/track-event`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                kind: 'recommendation_impression',
                from_path: window.location.pathname,
                to_division: it.division,
                item_id: it.id,
              }),
              keepalive: true,
            }).catch(() => { /* best-effort */ });
          });
        }
      })
      .catch(() => { /* network blip — section just stays hidden */ });

    return () => { cancelled = true; };
  }, [division, sessionId]);

  const handleClick = (item) => {
    if (getCookieConsent()?.choice !== 'accepted') return;
    fetch(`${API}/analytics/track-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind: 'recommendation_click',
        from_path: window.location.pathname,
        to_division: item.division,
        item_id: item.id,
      }),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  };

  if (!items.length) return null;

  return (
    <section
      data-testid="recommended-for-you"
      className="border-t border-gray-100 bg-white"
    >
      <div className="zh-container py-12 md:py-14">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-zinc-700">
              <Sparkles className="w-3 h-3" />
              {personalised ? 'Personalised picks' : 'Recommended for you'}
            </span>
            <h2 className="mt-2 text-2xl md:text-[26px] font-extrabold text-gray-900 leading-tight">
              {heading || 'Worth a look while you\u2019re here'}
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {items.map((item) => {
            const accent = DIVISION_ACCENTS[item.division] || DIVISION_ACCENTS.home;
            return (
              <Link
                key={item.id}
                to={item.href}
                onClick={() => handleClick(item)}
                data-testid={`recommended-card-${item.id}`}
                className="group block rounded-2xl border border-gray-200 bg-white p-5 hover:border-[#f5a623] hover:shadow-[0_8px_24px_-12px_rgba(245,166,35,0.35)] transition-all duration-200"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold px-2 py-0.5 rounded-full"
                    style={{ background: `${accent.color}1a`, color: accent.color }}
                  >
                    {accent.tag}
                  </span>
                  {item.reason && (
                    <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-gray-400">
                      {item.reason}
                    </span>
                  )}
                </div>
                <h3 className="text-[16px] font-extrabold text-gray-900 leading-tight mb-2">
                  {item.label}
                </h3>
                <p className="text-[13.5px] text-gray-600 leading-relaxed mb-4">{item.blurb}</p>
                <div className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wider text-zinc-700 group-hover:text-zinc-700">
                  Take a look <ArrowUpRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
